import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { ShipmentGroup } from "../types";

function wrapText(
  text: string,
  font: any,
  fontSize: number,
  maxWidth: number,
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? currentLine + " " + word : word;
    const width = font.widthOfTextAtSize(testLine, fontSize);
    if (width <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);

  return lines;
}

export async function createGroupManifestPdf(
  data: ShipmentGroup,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();

  pdfDoc.setTitle(`Group Manifest - ${data.groupNumber}`);
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject(`Group Manifest for ${data.groupNumber}`);
  pdfDoc.setCreationDate(new Date());

  const page = pdfDoc.addPage([595, 842]);
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const margin = 40;
  let yPos = height - margin;

  const black = rgb(0, 0, 0);
  const gray = rgb(0.4, 0.4, 0.4);
  const lightGray = rgb(0.95, 0.95, 0.95);
  const headerGray = rgb(0.9, 0.9, 0.9);

  // ===== ORDER FIX =====
  const orderedShipments = [...data.shipments].sort(
    (a, b) => a.order - b.order,
  );

  const firstShipment = orderedShipments[0];
  const lastShipment =
    orderedShipments.length > 0
      ? orderedShipments[orderedShipments.length - 1]
      : undefined;

  // ===== TITLE =====
  const title = `Group Manifest - ${data.groupNumber}`;
  const titleWidth = boldFont.widthOfTextAtSize(title, 18);
  page.drawText(title, {
    x: (width - titleWidth) / 2,
    y: yPos,
    size: 18,
    font: boldFont,
    color: black,
  });
  yPos -= 40;

  // ===== HEADER INFO =====
  const headerItems: { label: string; value: string }[] = [
    {
      label: "Truck License Plate",
      value: data.truckLicensePlate?.registrationNumber || "-",
    },
    {
      label: "Trailer License Plate",
      value: data.trailerLicensePlate?.registrationNumber || "-",
    },
    {
      label: "Driver Name",
      value: data.assignedDriver
        ? `${data.assignedDriver.firstName} ${data.assignedDriver.lastName}`
        : "-",
    },
  ];

  for (const item of headerItems) {
    page.drawText(`${item.label}:`, {
      x: margin,
      y: yPos,
      size: 10,
      font,
      color: gray,
    });

    page.drawText(item.value, {
      x: margin + 130,
      y: yPos,
      size: 10,
      font: boldFont,
      color: black,
    });

    yPos -= 20;
  }

  yPos -= 10;

  // ===== COLUMN DEFINITIONS =====
  // Total usable width: 595 - 2*40 = 515px
  // id(45) + ref(80) + pickup(110) + delivery(110) + qty(40) + weight(70) + volume(60) = 515
  const colWidths = {
    id: 45,
    ref: 80,
    pickup: 110,
    delivery: 110,
    qty: 40,
    weight: 70,
    volume: 60,
  };

  const tableWidth = width - 2 * margin;
  const rowHeight = 30;

  // ===== TABLE HEADER =====
  const headerY = yPos;

  page.drawRectangle({
    x: margin,
    y: headerY - rowHeight + 5,
    width: tableWidth,
    height: rowHeight,
    color: headerGray,
  });

  let xOffset = margin + 6;

  const drawHeaderCell = (label: string, colWidth: number) => {
    page.drawText(label, {
      x: xOffset,
      y: headerY - 18,
      size: 9,
      font: boldFont,
      color: gray,
    });
    xOffset += colWidth;
  };

  drawHeaderCell("ID", colWidths.id);
  drawHeaderCell("REF", colWidths.ref);
  drawHeaderCell("Pickup", colWidths.pickup);
  drawHeaderCell("Delivery", colWidths.delivery);
  drawHeaderCell("QTY", colWidths.qty);
  drawHeaderCell("Weight(kg)", colWidths.weight);
  drawHeaderCell("Vol(m3)", colWidths.volume);

  page.drawLine({
    start: { x: margin, y: headerY - rowHeight + 5 },
    end: { x: margin + tableWidth, y: headerY - rowHeight + 5 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });

  yPos = headerY - rowHeight;

  // ===== ROWS =====
  let totalQty = 0;
  let totalWeight = 0;
  let totalVolume = 0;

  const shipmentsLen = orderedShipments.length;

  for (let i = 0; i < shipmentsLen; i++) {
    const { shipment } = orderedShipments[i];

    const pickupLines = wrapText(
      shipment.pickup?.companyName || "-",
      font,
      9,
      colWidths.pickup - 8,
    );
    const deliveryLines = wrapText(
      shipment.delivery?.companyName || "-",
      font,
      9,
      colWidths.delivery - 8,
    );
    const refLines = wrapText(
      shipment.referenceNumber || "-",
      font,
      9,
      colWidths.ref - 8,
    );

    const lineCount = Math.max(
      pickupLines.length,
      deliveryLines.length,
      refLines.length,
      1,
    );
    const dynamicRowHeight = Math.max(rowHeight, lineCount * 14 + 10);

    yPos -= dynamicRowHeight;

    // Alternate row background
    if ((i & 1) === 1) {
      page.drawRectangle({
        x: margin,
        y: yPos + 5,
        width: tableWidth,
        height: dynamicRowHeight,
        color: lightGray,
      });
    }

    const rowTextY = yPos + dynamicRowHeight - 18;
    xOffset = margin + 6;

    // ── ID ──
    page.drawText(shipment.shipmentNumber, {
      x: xOffset,
      y: rowTextY,
      size: 9,
      font,
      color: black,
    });
    xOffset += colWidths.id;

    // ── REF ──
    for (let j = 0; j < refLines.length; j++) {
      page.drawText(refLines[j], {
        x: xOffset,
        y: rowTextY - j * 12,
        size: 9,
        font,
        color: black,
      });
    }
    xOffset += colWidths.ref;

    // ── Pickup ──
    for (let j = 0; j < pickupLines.length; j++) {
      page.drawText(pickupLines[j], {
        x: xOffset,
        y: rowTextY - j * 12,
        size: 9,
        font,
        color: black,
      });
    }
    xOffset += colWidths.pickup;

    // ── Delivery ──
    for (let j = 0; j < deliveryLines.length; j++) {
      page.drawText(deliveryLines[j], {
        x: xOffset,
        y: rowTextY - j * 12,
        size: 9,
        font,
        color: black,
      });
    }
    xOffset += colWidths.delivery;

    // ── QTY ──
    const qty = shipment.totalQuantity || 0;
    totalQty += qty;
    page.drawText(String(qty), {
      x: xOffset,
      y: rowTextY,
      size: 9,
      font,
      color: black,
    });
    xOffset += colWidths.qty;

    // ── Weight ──
    const weight = shipment.totalWeight || 0;
    totalWeight += weight;
    page.drawText(weight.toLocaleString("en-US"), {
      x: xOffset,
      y: rowTextY,
      size: 9,
      font,
      color: black,
    });
    xOffset += colWidths.weight;

    // ── Volume ──
    const volume = shipment.totalVolume || 0;
    totalVolume += volume;
    page.drawText(volume.toString(), {
      x: xOffset,
      y: rowTextY,
      size: 9,
      font,
      color: black,
    });

    // Row divider
    page.drawLine({
      start: { x: margin, y: yPos + 5 },
      end: { x: margin + tableWidth, y: yPos + 5 },
      thickness: 0.5,
      color: rgb(0.85, 0.85, 0.85),
    });
  }

  // ===== TOTALS ROW =====
  yPos -= rowHeight;
  page.drawRectangle({
    x: margin,
    y: yPos + 5,
    width: tableWidth,
    height: rowHeight,
    color: headerGray,
  });

  xOffset = margin + 6;

  page.drawText("TOTALS:", {
    x: xOffset,
    y: yPos + 12,
    size: 10,
    font: boldFont,
    color: black,
  });

  // Skip id + ref + pickup + delivery columns
  xOffset +=
    colWidths.id + colWidths.ref + colWidths.pickup + colWidths.delivery;

  page.drawText(String(totalQty), {
    x: xOffset,
    y: yPos + 12,
    size: 10,
    font: boldFont,
    color: black,
  });
  xOffset += colWidths.qty;

  page.drawText(`${totalWeight.toLocaleString("en-US")}kg`, {
    x: xOffset,
    y: yPos + 12,
    size: 10,
    font: boldFont,
    color: black,
  });
  xOffset += colWidths.weight;

  page.drawText(`${totalVolume}m3`, {
    x: xOffset,
    y: yPos + 12,
    size: 10,
    font: boldFont,
    color: black,
  });

  // ===== DATES =====
  yPos -= 50;

  const departureDate = firstShipment?.shipment?.pickup?.date
    ? firstShipment.shipment.pickup.date.split("T")[0]
    : "-";

  page.drawText("Date of Departure:", {
    x: margin,
    y: yPos,
    size: 11,
    font,
    color: black,
  });
  page.drawText(departureDate, {
    x: margin + 130,
    y: yPos,
    size: 11,
    font: boldFont,
    color: black,
  });
  page.drawText("(earliest pickup date)", {
    x: margin,
    y: yPos - 14,
    size: 9,
    font,
    color: gray,
  });

  yPos -= 40;

  const arrivalDate = lastShipment?.shipment?.delivery?.date
    ? lastShipment.shipment.delivery.date.split("T")[0]
    : "-";

  page.drawText("Expected Date of Arrival:", {
    x: margin,
    y: yPos,
    size: 11,
    font,
    color: black,
  });
  page.drawText(arrivalDate, {
    x: margin + 160,
    y: yPos,
    size: 11,
    font: boldFont,
    color: black,
  });
  page.drawText("(latest delivery date)", {
    x: margin,
    y: yPos - 14,
    size: 9,
    font,
    color: gray,
  });

  return pdfDoc.save();
}

/* =========================
   DOWNLOAD HELPER
========================= */
export async function downloadGroupManifestPdf(
  data: ShipmentGroup,
  filename?: string,
): Promise<void> {
  try {
    const pdfBytes = await createGroupManifestPdf(data);
    const safeBytes = new Uint8Array(pdfBytes);
    const blob = new Blob([safeBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename || `Group-Manifest-${data.groupNumber}.pdf`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading Group Manifest:", error);
    throw error;
  }
}
